import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  ChevronLeft, Users, Settings, Plus, Trash2, Check, X,
  Loader2, Save, Phone, Mail, GripVertical, Edit2,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useQuery } from "../../lib/useQuery";
import { supabase } from "../../lib/supabase";
import { useToast } from "../Toast";
import LoadingSkeleton from "../LoadingSkeleton";

const DEFAULT_FOLLOWUP_TYPES = [
  { name: "15 días: Llamada Informativa", sort_order: 1 },
  { name: "15 días: Llamada Satisfacción Inicial", sort_order: 2 },
  { name: "60 días: Llamada Aprovechamiento servicios", sort_order: 3 },
  { name: "Check 4: Email", sort_order: 4 },
  { name: "Check 5: Llamada", sort_order: 5 },
];

// Limpia el teléfono y devuelve la URL de WhatsApp, o null si el número no es válido.
function buildWhatsAppUrl(phone, message = "¡Hola!") {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 6) return null;
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}

function WhatsAppIcon({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.247-.694.247-1.289.173-1.413-.074-.124-.272-.198-.57-.347M12.05 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0 0 20.464 3.488"/>
    </svg>
  );
}

function ConfigDrawer({ types, followups, onSave, onClose, saving }) {
  const [items, setItems] = useState(types.map(t => ({ ...t })));
  const [visible, setVisible] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => setVisible(true));
    return () => { document.body.style.overflow = ""; };
  }, []);

  const close = () => {
    setVisible(false);
    setTimeout(onClose, 200);
  };

  const addItem = () => {
    const maxOrder = items.length > 0 ? Math.max(...items.map(i => i.sort_order)) : 0;
    setItems([...items, { id: `new-${Date.now()}`, name: "", sort_order: maxOrder + 1, isNew: true }]);
  };

  const updateItem = (idx, field, value) => {
    const newItems = [...items];
    newItems[idx] = { ...newItems[idx], [field]: value };
    setItems(newItems);
  };

  const removeItem = (idx) => {
    const item = items[idx];
    if (!item.isNew) {
      const usedBy = (followups || []).filter(f => f.followup_type_id === item.id && f.completed);
      if (usedBy.length > 0) {
        setDeleteError(`No se puede eliminar "${item.name}" porque tiene ${usedBy.length} check(s) marcado(s). Desmárcalos primero.`);
        return;
      }
    }
    setDeleteError(null);
    setItems(items.filter((_, i) => i !== idx));
  };

  const handleSave = () => {
    const validItems = items.filter(i => i.name.trim());
    onSave(validItems);
  };

  const content = (
    <>
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <Settings size={18} className="text-brand" />
          Configurar Interacciones
        </h3>
        <button onClick={close} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
          <X size={18} className="text-gray-400" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <p className="text-sm text-gray-500 mb-4">
          Define las interacciones de seguimiento que aparecerán para todos los clientes.
        </p>

        {deleteError && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
            {deleteError}
          </div>
        )}

        {items.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">No hay interacciones configuradas</p>
        ) : (
          <div className="space-y-2">
            {items.map((item, idx) => (
              <div key={item.id || idx} className="flex items-center gap-2">
                <GripVertical size={16} className="text-gray-300 shrink-0" />
                <input
                  type="text"
                  value={item.name}
                  onChange={(e) => updateItem(idx, "name", e.target.value)}
                  placeholder="Nombre de la interacción"
                  className="flex-1 px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-brand"
                />
                <button
                  onClick={() => removeItem(idx)}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        <button
          onClick={addItem}
          className="mt-4 w-full py-2.5 text-sm text-brand border border-dashed border-brand/40 rounded-lg hover:bg-brand-light flex items-center justify-center gap-1.5 transition-colors"
        >
          <Plus size={16} /> Añadir interacción
        </button>
      </div>

      <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
        <button
          onClick={close}
          className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
        >
          Cancelar
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-brand hover:bg-brand-dark rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Guardar
        </button>
      </div>
    </>
  );

  const drawer = (
    <div className="fixed inset-0 z-[99999]">
      <div
        className={`fixed inset-0 bg-black/40 transition-opacity duration-200 ${visible ? "opacity-100" : "opacity-0"}`}
        onClick={close}
      />

      {/* Desktop: drawer from right */}
      <div
        className={`
          hidden md:flex fixed top-0 right-0 h-full w-full max-w-md
          flex-col bg-white shadow-2xl
          transition-transform duration-200 ease-out
          ${visible ? "translate-x-0" : "translate-x-full"}
        `}
      >
        {content}
      </div>

      {/* Mobile: drawer from bottom */}
      <div
        className={`
          md:hidden fixed inset-0 bg-white
          flex flex-col
          transition-transform duration-200 ease-out
          ${visible ? "translate-y-0" : "translate-y-full"}
        `}
      >
        {content}
      </div>
    </div>
  );

  return createPortal(drawer, document.body);
}

export default function SeguimientoClientes({ onBack }) {
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const [showConfig, setShowConfig] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updatingFollowup, setUpdatingFollowup] = useState(null);

  const { data: followupTypes, loading: loadingTypes, refetch: refetchTypes } = useQuery(async () => {
    const { data, error } = await supabase
      .from("followup_types")
      .select("*")
      .eq("student_id", user.id)
      .order("sort_order");
    if (error) throw error;
    return data || [];
  }, [user.id]);

  const { data: clients, loading: loadingClients, refetch: refetchClients } = useQuery(async () => {
    const { data, error } = await supabase
      .from("leads")
      .select("*")
      .eq("student_id", user.id)
      .in("resultado", ["cliente", "recuperado"])
      .order("fecha_resultado", { ascending: false });
    if (error) throw error;
    return data || [];
  }, [user.id]);

  const { data: followups, loading: loadingFollowups, refetch: refetchFollowups } = useQuery(async () => {
    const { data, error } = await supabase
      .from("client_followups")
      .select("*")
      .eq("student_id", user.id);
    if (error) throw error;
    return data || [];
  }, [user.id]);

  useEffect(() => {
    if (!loadingTypes && followupTypes && followupTypes.length === 0) {
      initializeDefaultTypes();
    }
  }, [loadingTypes, followupTypes]);

  const initializeDefaultTypes = async () => {
    const inserts = DEFAULT_FOLLOWUP_TYPES.map(t => ({
      student_id: user.id,
      name: t.name,
      sort_order: t.sort_order,
    }));
    const { error } = await supabase.from("followup_types").insert(inserts);
    if (!error) refetchTypes();
  };

  const handleSaveConfig = async (items) => {
    setSaving(true);
    try {
      const existingIds = (followupTypes || []).map(t => t.id);
      const newItemIds = items.filter(i => !i.isNew).map(i => i.id);
      const toDelete = existingIds.filter(id => !newItemIds.includes(id));

      if (toDelete.length > 0) {
        await supabase.from("followup_types").delete().in("id", toDelete);
      }

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.isNew) {
          await supabase.from("followup_types").insert({
            student_id: user.id,
            name: item.name,
            sort_order: i + 1,
          });
        } else {
          await supabase.from("followup_types").update({
            name: item.name,
            sort_order: i + 1,
          }).eq("id", item.id);
        }
      }

      await refetchTypes();
      showSuccess("Configuración guardada");
      setShowConfig(false);
    } catch (err) {
      showError("No se pudo guardar la configuración");
    } finally {
      setSaving(false);
    }
  };

  const toggleFollowup = async (leadId, typeId, currentCompleted) => {
    const key = `${leadId}-${typeId}`;
    setUpdatingFollowup(key);

    try {
      const existing = followups?.find(f => f.lead_id === leadId && f.followup_type_id === typeId);

      if (existing) {
        await supabase.from("client_followups").update({
          completed: !currentCompleted,
          completed_at: !currentCompleted ? new Date().toISOString() : null,
        }).eq("id", existing.id);
      } else {
        await supabase.from("client_followups").insert({
          student_id: user.id,
          lead_id: leadId,
          followup_type_id: typeId,
          completed: true,
          completed_at: new Date().toISOString(),
        });
      }
      await refetchFollowups();
    } catch (err) {
      showError("Error al actualizar");
    } finally {
      setUpdatingFollowup(null);
    }
  };

  const isFollowupCompleted = (leadId, typeId) => {
    const followup = followups?.find(f => f.lead_id === leadId && f.followup_type_id === typeId);
    return followup?.completed || false;
  };

  const loading = loadingTypes || loadingClients || loadingFollowups;
  if (loading) return <LoadingSkeleton />;

  return (
    <div className="animate-fade-in">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-gray-500 hover:text-gray-700 text-sm mb-6"
      >
        <ChevronLeft size={18} /> Volver a Herramientas
      </button>

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
            <Users size={24} className="text-emerald-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Seguimiento Clientes</h2>
            <p className="text-sm text-gray-500">{clients?.length || 0} cliente(s) en seguimiento</p>
          </div>
        </div>

        <button
          onClick={() => setShowConfig(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <Settings size={16} />
          Configurar Interacciones
        </button>
      </div>

      {(!followupTypes || followupTypes.length === 0) ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <p className="text-gray-500 mb-4">No hay interacciones configuradas</p>
          <button
            onClick={() => setShowConfig(true)}
            className="px-4 py-2 text-sm bg-brand text-white rounded-lg hover:bg-brand-dark"
          >
            Configurar ahora
          </button>
        </div>
      ) : (!clients || clients.length === 0) ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <Users size={48} className="text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No hay clientes todavía</p>
          <p className="text-sm text-gray-400 mt-1">Los leads con resultado "Cliente" aparecerán aquí</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Cliente</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Fecha Alta</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Servicio</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Contacto</th>
                  {followupTypes.map((type) => (
                    <th key={type.id} className="text-center px-2 py-3 font-medium text-gray-700 min-w-[100px]">
                      <span className="text-xs leading-tight block">{type.name}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {clients.map((client) => (
                  <tr key={client.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                      {client.nombre}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {client.fecha_resultado ? new Date(client.fecha_resultado).toLocaleDateString("es-ES") : "-"}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {client.servicio || "-"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        {client.telefono && (
                          <a href={`tel:${client.telefono}`} className="flex items-center gap-1 hover:text-brand">
                            <Phone size={12} /> {client.telefono}
                          </a>
                        )}
                        {buildWhatsAppUrl(client.telefono) && (
                          <a
                            href={buildWhatsAppUrl(client.telefono)}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Abrir WhatsApp"
                            className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500 text-white hover:bg-emerald-600 transition-colors"
                          >
                            <WhatsAppIcon size={12} />
                          </a>
                        )}
                        {client.email && (
                          <a href={`mailto:${client.email}`} className="flex items-center gap-1 hover:text-brand">
                            <Mail size={12} />
                          </a>
                        )}
                      </div>
                    </td>
                    {followupTypes.map((type) => {
                      const completed = isFollowupCompleted(client.id, type.id);
                      const isUpdating = updatingFollowup === `${client.id}-${type.id}`;
                      return (
                        <td key={type.id} className="px-2 py-3 text-center">
                          <button
                            onClick={() => toggleFollowup(client.id, type.id, completed)}
                            disabled={isUpdating}
                            className={`w-6 h-6 rounded border-2 flex items-center justify-center mx-auto transition-all ${
                              completed
                                ? "bg-emerald-500 border-emerald-500 text-white"
                                : "border-gray-300 hover:border-brand"
                            } ${isUpdating ? "opacity-50" : ""}`}
                          >
                            {isUpdating ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : completed ? (
                              <Check size={14} />
                            ) : null}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showConfig && (
        <ConfigDrawer
          types={followupTypes || []}
          followups={followups || []}
          onSave={handleSaveConfig}
          onClose={() => setShowConfig(false)}
          saving={saving}
        />
      )}
    </div>
  );
}
